import React from 'react';
import { CountryTemplate } from '../../components/CountryTemplate';

export const PanamaPage = () => {
  const panamaData = {
    name: 'Panama',
    region: 'Latin America',
    color: '#C85A54',
    metaTitle: 'Panama Pharma & Nutra Registration | TheApostrophe',
    metaDescription: 'Enter Panama with CTD/ACTD-ready pharma and nutra dossiers. We help you navigate MINSA, Law 419/2024 and fast-track routes for high-standard products.',
    
    hero: {
      title: 'Pharma & Nutraceutical Registration in Panama',
      subtitle: 'Leverage Panama\'s logistics hub and modernised health laws with CTD/ACTD-ready dossiers that match MINSA expectations from Day 1.',
      cta: 'Plan My Panama Regulatory Entry'
    },
    
    atAGlance: [
      { label: 'Population', value: '~4.5 million (2024)' },
      { label: 'Income Level', value: 'High-income, services-driven economy' },
      { label: 'Health Spending', value: '~9–10% of GDP (current health expenditure, 2021)' },
      { label: 'Regulator', value: 'MINSA – Dirección Nacional de Farmacia y Drogas (DNFD)' },
      { label: 'Primary Law', value: 'Law 1/2001, updated by Law 419/2024 and Executive Decrees' },
      { label: 'Key Update', value: 'Law 419/2024 fast-tracks products from high-standard authorities' }
    ],
    
    marketOverview: {
      title: 'Market Overview: Why Panama Matters',
      stats: [
        'Panama is one of Latin America\'s most connected logistics hubs with a dollar-based economy',
        'The Panama Canal and Colón Free Trade Zone drive a service-based model (70%+ of GDP)',
        'Pharmaceutical market expanding due to ageing, chronic disease and demand for quality generics',
        'Imports dominate medicines market; Panama used as regional re-export hub via Colón Free Zone',
        'Law 419/2024 modernises regulation and fast-tracks products from High Standard Countries (US, EU)',
        'Pharmaceuticals among main products traded through Colón Free Zone infrastructure'
      ],
      opportunities: [
        'Direct entry into Panama\'s own market via DNFD registration and private distribution',
        'Regional distribution hub for Central America, Caribbean and parts of South America',
        'Logistics and free-zone infrastructure enables efficient regional reach',
        'Modernised regulatory framework reduces barriers for compliant exporters'
      ]
    },
    
    diseaseburden: {
      title: 'Disease Burden & Demand Drivers',
      subtitle: 'High NCD burden, urbanisation and robust private healthcare sector create strong demand for chronic therapy generics and preventive wellness products.',
      categories: [
        {
          name: 'Non-Communicable Diseases & Metabolic Health',
          items: [
            'Obesity: ~30.6% of adult women and 20.4% of adult men',
            'Diabetes: ~11% of adult women and ~10% of adult men (national surveys ~12%)',
            'Ischemic heart disease, stroke and diabetes among top causes of death',
            'Rising NCD burden drives demand for chronic therapies',
            'Pharma demand: Antidiabetics, antihypertensives, statins, lipid-lowering agents',
            'Pharma demand: Cardiovascular medicines, chronic disease management',
            'Nutra demand: Cardiometabolic supplements, omega-3, CoQ10, inositols, chromium'
          ]
        },
        {
          name: 'Urban Middle Class & Premium Health Market',
          items: [
            'High urbanisation and services-based economy create sizeable private market',
            'Relatively high health spending per capita supports premium products',
            'Private-clinic and pharmacy channels with willingness to pay for quality',
            'Women\'s health focus: iron-folate, prenatal vitamins, PCOS-focused nutra',
            'Geriatric & bone health: vitamin D, calcium, B-complex, joint health',
            'GI & liver support: PPIs, H2 blockers, digestive enzymes, hepatoprotective formulas',
            'Branded generics and premium supplements perform well in urban centers'
          ]
        }
      ]
    },
    
    regulatoryFramework: {
      authority: 'Ministry of Health (MINSA) – National Directorate of Pharmacy and Drugs (DNFD)',
      description: 'DNFD is responsible for sanitary registration, pharmacovigilance and market control of medicines and health products. Core legal framework includes Law 1/2001 (general medicines framework), Executive Decree 13/2023 (detailed registration requirements), Law 419/2024 (modernisation with expedited routes for high-standard products), and Executive Decree 2/2025 (simplified registration for products from high-standard authorities).',
      pharma: {
        timeline: 'Timelines depend on product type and route. Law 419/2024 and Decree 2/2025 aim to shorten timelines for products already approved by high-standard authorities (US FDA, EMA etc.), though exact durations vary based on documentation quality and regulator workload. Standard registrations can take several months.',
        requirements: [
          'Local legal representative and importer required to interface with DNFD',
          'Panama-compliant registration dossier per Law 1/2001 and Executive Decree 13/2023',
          'CTD-like structure widely accepted, covering quality, safety and efficacy modules',
          'Expedited routes available for products from high-standard authorities under Law 419/2024',
          'Administrative: application form, power of attorney, legal representative proof, fees',
          'Product details: brand name, INN(s), dosage form, strength(s), formula',
          'Quality: manufacturing process, specifications, CoAs, stability data, GMP evidence',
          'Clinical/non-clinical: literature or summaries for generics, more extensive for new products',
          'Spanish labelling: outer carton, blister/bottle labels, patient leaflet',
          'DNFD evaluates safety, efficacy and quality; expedited route focuses on foreign approval verification',
          'Registro Sanitario issued and listed in MINSA online registry upon approval',
          'Registration valid for defined period (commonly 5 years) with renewal required'
        ],
        insight: 'Law 419/2024 represents a major regulatory modernisation. Products with US FDA or EMA approval can benefit from expedited registration with reduced duplication of clinical assessment. The focus shifts to verification of authenticity and local compliance (labelling, sourcing). CTD/ACTD-ready dossiers align well with DNFD expectations.'
      },
      nutra: {
        timeline: 'Classification-dependent. Products with therapeutic positioning or pharmacological doses require sanitary registration as "vitamin, dietetic and food supplements with therapeutic properties" - more extensive than simple food registration. Timelines vary based on complexity and documentation quality.',
        requirements: [
          'Classification critical: therapeutic positioning triggers pharmaceutical-style supplement registration',
          'Resolution on vitamin/dietetic/food supplements (2019) with 2023 draft amendment',
          'Products with therapeutic properties must obtain sanitary registration with MINSA',
          'Full product composition, formula and per-unit dosing required',
          'Evidence on ingredient safety: monographs, GRAS lists, EFSA/FDA references',
          'Manufacturing and quality documentation: GMP, stability, CoAs',
          'Spanish labelling: ingredient list, daily dose, warnings, permitted claims',
          'Proof that ingredients and claims do not convert product into medicine category',
          'Disease-treatment claims increase scrutiny; safer positioning is supportive/functional claims',
          'Products targeting diabetes, heart health, weight control require careful wording and evidence',
          'Changes in composition, labelling or packaging may require prior approval'
        ],
        insight: 'Panama treats therapeutic supplements more stringently than many LATAM markets. Products positioned for health benefits with dosage forms similar to medicines fall into a hybrid category requiring registration similar to pharmaceuticals. Early classification assessment is critical - mis-classification causes significant delays. Working with evidence-backed claims and transparent ingredient disclosure ensures smoother approval.'
      }
    },
    
    pharmaPathway: {
      title: 'Pharmaceutical Registration Pathway (Medicines)',
      steps: [
        'Appoint Local Legal Representative & Importer - Required to interface with DNFD, submit dossiers and hold sanitary registration',
        'Prepare Panama-Compliant Registration Dossier - Per Law 1/2001 and Executive Decree 13/2023',
        'CTD-Like Structure Accepted - Authorities accept CTD-aligned structure covering quality, safety, efficacy',
        'Fast-Track for High-Standard Products - Law 419/2024 and Decree 2/2025 create expedited routes for US FDA, EMA approvals',
        'Administrative Documentation - Application form, power of attorney, legal representative proof, fees',
        'Product Details - Brand name, INN(s), dosage form, strength(s), qualitative/quantitative formula',
        'Quality Documentation - Manufacturing process, specifications, CoAs, stability data, GMP evidence for API and FDF',
        'Clinical/Non-Clinical Data - Published literature or clinical summaries for generics; extensive data for new products',
        'Spanish Labelling & Artwork - Outer carton, blister/bottle labels, patient leaflet in Spanish',
        'Filing with DNFD - Submit complete dossier for technical evaluation',
        'Technical Evaluation - DNFD evaluates safety, efficacy, quality; expedited route verifies foreign approvals',
        'Approval & Registro Sanitario - Certificate issued and listed in MINSA online registry',
        'Post-Approval Lifecycle - Variations, renewals and pharmacovigilance per DNFD procedures'
      ]
    },
    
    nutraPathway: {
      title: 'Nutraceutical, Vitamin & Food Supplement Registration',
      steps: [
        'Classification Check - Therapeutic positioning or pharmacological doses = sanitary registration required',
        'Category: Vitamin, Dietetic & Food Supplements with Therapeutic Properties - Falls under MINSA registration',
        'Applicable Framework - Resolution 2019 with 2023 draft amendment aligned with Law 1/2001',
        'Product Composition - Full formula and per-unit dosing disclosure',
        'Ingredient Safety Evidence - Monographs, GRAS/approved lists, EFSA or FDA references',
        'Manufacturing & Quality Documentation - GMP certificates, stability data, CoAs',
        'Spanish Labelling Requirements - Ingredient list, recommended daily dose, warnings, compliant claims',
        'Claims Positioning - Supportive/functional claims preferred; disease-treatment claims increase scrutiny',
        'Evidence-Backed Claims - Products for diabetes, heart health, weight control need careful wording and evidence',
        'Avoid Medicine Classification - Ensure ingredients and claims don\'t trigger pharmaceutical category',
        'Renewals & Modifications - Changes in composition, labelling or packaging require prior approval',
        'Registration with MINSA - Obtain sanitary registration certificate for market entry'
      ]
    },
    
    requiredDocs: {
      'Pharmaceutical Products': [
        'Application form and power of attorney',
        'Proof of local legal representative',
        'Product details: brand name, INN(s), dosage form, strength(s), formula',
        'Manufacturing process and specifications',
        'Certificates of Analysis (CoAs)',
        'Stability data under acceptable climatic conditions',
        'GMP evidence for API and finished dosage form',
        'Clinical literature or summaries (for generics)',
        'Spanish labelling: carton, labels, patient leaflet',
        'CoPP or proof of registration in reference markets (for fast-track)',
        'Registration fees payment proof'
      ],
      'Nutraceuticals / Vitamin & Food Supplements': [
        'Complete product composition and formula',
        'Per-unit dosing details',
        'Ingredient safety evidence (monographs, GRAS lists, regulatory references)',
        'Manufacturing GMP certificates',
        'Stability data and CoAs',
        'Spanish labelling with ingredient list, daily dose, warnings',
        'Claims documentation within permitted therapeutic/nutritional scope',
        'Evidence that product does not qualify as medicine',
        'Local importer/representative documentation',
        'Application form and fees'
      ]
    },
    
    whyThisCountry: {
      title: 'Why Panama Is Attractive for Your Portfolio',
      reasons: [
        'Gateway Hub for Region - Colón Free Zone is largest in Western Hemisphere; pharmaceuticals among main product groups for redistribution',
        'Modernised Regulatory Framework - Law 419/2024 and Decree 2/2025 streamline registration and cut timelines for high-standard products',
        'Dollar-Based Economy - Low inflation, stable currency reduces financial risk for exporters',
        'Strong Health Spending - 9-10% of GDP with significant private sector creates space for premium products',
        'Strategic Logistics Infrastructure - Panama Canal and free-trade zones enable efficient regional distribution',
        'Growing Natural & Herbal Demand - Increasing interest in nutraceuticals provided they meet registration standards',
        'Both Market & Hub - Functions as national market and distribution base for Central America/Caribbean'
      ],
      bestPortfolio: [
        'Pharma: CTD/ACTD-ready generics in NCD segments (cardiovascular, diabetes, CNS, GI), branded generics with US/EU approval for fast-track entry',
        'Nutra: High-quality supplements in cardio-metabolic health, women\'s health, joint health with evidence-backed claims; premium multivitamins and wellness SKUs for insured urban consumers'
      ]
    },
    
    howWeHelp: {
      title: 'How TheApostrophe Helps You in Panama',
      services: [
        'Regulatory Strategy & Product Mapping - Country-fit assessment and SKU prioritisation based on disease burden, competition and re-export potential via Panama',
        'CTD/ACTD Dossier Development - Gap analysis between existing dossiers and Panama requirements (Law 1/2001, Decree 13/2023, Law 419/2024); full CTD/ACTD compilation and technical writing',
        'Supplements & Vitamin Registration Support - Classification assessment (medicine vs. dietetic supplement vs. food); ingredient acceptability checks and claim-safe labelling',
        'Artwork, Translation & Labelling Compliance - Spanish artwork aligned with MINSA labelling rules; coordination of mock-ups, pack sizes and local traceability needs',
        'Fast-Track Application Support - Leverage Law 419/2024 expedited routes for products approved by high-standard authorities',
        'Lifecycle Management & Queries - Support in addressing MINSA/DNFD observations, renewals, post-approval variations and documentation updates',
        'Regional Hub Strategy - Leverage Panama registration for Central America and Caribbean expansion via Colón Free Zone infrastructure'
      ]
    },
    
    faqs: [
      {
        question: 'Does Panama accept CTD or ACTD format dossiers?',
        answer: 'Panama\'s requirements are based on Law 1/2001 and its decrees, but in practice a CTD-like structure is widely accepted for medicines, especially when referencing approvals in high-standard markets. International regulatory providers routinely support CTD Module 3-type submissions for Panama.'
      },
      {
        question: 'How long does registration usually take?',
        answer: 'Timelines depend on product type and route. Law 419/2024 and Decree 2/2025 intend to shorten timelines for products already approved by high-standard authorities, but exact durations vary based on documentation quality and regulator workload. Standard registrations can take several months.'
      },
      {
        question: 'Can nutraceuticals be registered as simple foods?',
        answer: 'If your product has therapeutic positioning, pharmacological-type doses, or combination formulas, it is more likely to fall under the regime for vitamin, dietetic and food supplements with therapeutic properties, requiring sanitary registration and stricter documentation similar to pharmaceutical supplements.'
      },
      {
        question: 'Is Panama a good starting point for Central America?',
        answer: 'Yes. Panama combines modern regulation, a dollar-based economy and powerful free-trade zones. For many companies it functions as both a national market and a distribution hub into neighbouring countries, making it an ideal regional gateway.'
      },
      {
        question: 'What is the benefit of Law 419/2024 for products with US or EU approval?',
        answer: 'Law 419/2024 and Executive Decree 2/2025 create expedited registration routes for products already approved by high-standard authorities like US FDA or EMA. This fast-track reduces duplication of clinical assessment and focuses on verification of foreign approvals and local compliance, significantly shortening time to market.'
      }
    ]
  };

  return <CountryTemplate countryData={panamaData} />;
};
